import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { OrgUnitRow, OrgUnitQuery } from "@/api/management";
import { useOrgUnitsScroll } from "@/hooks/useManagementLists";
import { orgNodeFromRow } from "@/lib/org-unit-row";
import type { OrgTreeNode, ScopeVerdict } from "@/lib/org-structure-tree";
import type { OrgSelection } from "@/components/agents/configuration-server/OrgStructureListView";
import { OrgDecisionMap } from "./OrgDecisionMap";
import { LoadMore } from "./Paging";
import { StageLoading } from "./StageLoading";
import { useDraftVerdicts } from "./managementDraftContext";
import { overlayOrgForest } from "@/lib/management-draft";
function useMapPages(
  projectId: string,
  artifactId: string,
  query: OrgUnitQuery,
  enabled = true,
) {
  const pending = useOrgUnitsScroll(
    projectId,
    artifactId,
    { ...query, physical: true, verdict: "pending" },
    enabled,
  );
  const approved = useOrgUnitsScroll(
    projectId,
    artifactId,
    { ...query, physical: true, verdict: "approved" },
    enabled,
  );
  const excluded = useOrgUnitsScroll(
    projectId,
    artifactId,
    { ...query, physical: true, verdict: "out_of_scope" },
    enabled,
  );
  const rows = useMemo(
    () =>
      [pending.data, approved.data, excluded.data].flatMap(
        (data) => data?.pages.flatMap((page) => page.items) ?? [],
      ),
    [pending.data, approved.data, excluded.data],
  );
  return {
    rows,
    isPending: [pending, approved, excluded].some((page) => page.isPending),
    isError: [pending, approved, excluded].some((page) => page.isError),
    hasNextPage: [pending, approved, excluded].some((page) => page.hasNextPage),
    isFetchingNextPage: [pending, approved, excluded].some(
      (page) => page.isFetchingNextPage,
    ),
    fetchNextPage: () => {
      for (const page of [pending, approved, excluded])
        if (page.hasNextPage) void page.fetchNextPage();
    },
    refetch: () => {
      for (const page of [pending, approved, excluded]) void page.refetch();
    },
  };
}
export function BoundedOrgMap({
  projectId,
  artifactId,
  query,
  selection,
  onVerdict,
  onReturn,
}: {
  projectId: string;
  artifactId: string;
  query: OrgUnitQuery;
  selection: OrgSelection;
  onVerdict: (
    node: OrgTreeNode,
    verdict: Exclude<ScopeVerdict, "pending">,
  ) => void;
  onReturn: (node: OrgTreeNode) => void;
}) {
  const { t } = useTranslation();
  const roots = useMapPages(projectId, artifactId, query);
  const [children, setChildren] = useState<Record<string, OrgUnitRow[]>>({});
  const register = useCallback(
    (parent: string, rows: OrgUnitRow[]) =>
      setChildren((current) =>
        current[parent] === rows ? current : { ...current, [parent]: rows },
      ),
    [],
  );
  const filtered = !!(
    query.q?.trim() ||
    query.unitTypes?.length ||
    query.countries?.length ||
    query.origin
  );
  useEffect(() => {
    setChildren({});
  }, [query]);
  const rows = roots.rows;
  const draftVerdicts = useDraftVerdicts("org_structure");
  const forest = useMemo(() => {
    const all = filtered ? rows : [...rows, ...Object.values(children).flat()];
    const byKey = new Map(all.map((row) => [row.key, orgNodeFromRow(row)]));
    for (const row of all) {
      const node = byKey.get(row.key)!;
      const parent = row.parent_key ? byKey.get(row.parent_key) : undefined;
      if (
        parent &&
        parent !== node &&
        !parent.children.some((child) => child.key === node.key)
      )
        parent.children.push(node);
    }
    return overlayOrgForest(
      [...byKey.values()].filter(
        (node) =>
          !all.some(
            (row) =>
              row.key === node.key &&
              row.parent_key &&
              byKey.has(row.parent_key),
          ),
      ),
      draftVerdicts,
    );
  }, [rows, children, filtered, draftVerdicts]);
  return (
    <div>
      {roots.isPending ? (
        <StageLoading variant="list" />
      ) : roots.isError ? (
        <div role="alert">
          <button onClick={roots.refetch}>{t("Retry")}</button>
        </div>
      ) : (
        <>
          <p className="px-5 py-2 text-xs">
            {t("{{count}} loaded units", {
              count: new Set(
                [...rows, ...Object.values(children).flat()].map(
                  (row) => row.key,
                ),
              ).size,
            })}
          </p>
          <OrgDecisionMap
            roots={forest}
            matchKeys={null}
            selection={selection}
            onRequestVerdict={onVerdict}
            onReturn={onReturn}
          />
          <div className="border-t px-5 py-2">
            {!filtered &&
              rows.map((row) => (
                <MapBranch
                  key={row.key}
                  row={row}
                  projectId={projectId}
                  artifactId={artifactId}
                  register={register}
                />
              ))}
          </div>
          <LoadMore
            hasMore={!!roots.hasNextPage}
            loading={roots.isFetchingNextPage}
            onLoadMore={() => void roots.fetchNextPage()}
          />
        </>
      )}
    </div>
  );
}
function MapBranch({
  row,
  projectId,
  artifactId,
  register,
}: {
  row: OrgUnitRow;
  projectId: string;
  artifactId: string;
  register: (key: string, rows: OrgUnitRow[]) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const children = useMapPages(
    projectId,
    artifactId,
    { verdict: "pending", parent: row.key },
    open,
  );

  const rows = children.rows;
  useEffect(() => {
    register(row.key, rows);
  }, [row.key, rows, register]);
  if (!row.child_count) return null;
  return (
    <div className="border-l pl-3">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="py-2 text-sm"
      >
        {t(open ? "Collapse {{name}}" : "Expand {{name}}", {
          name: row.target_id ?? row.id,
        })}
      </button>
      {open && (
        <>
          {children.isPending ? (
            <StageLoading variant="panel" />
          ) : (
            rows.map((child) => (
              <MapBranch
                key={child.key}
                row={child}
                projectId={projectId}
                artifactId={artifactId}
                register={register}
              />
            ))
          )}
          <LoadMore
            hasMore={!!children.hasNextPage}
            loading={children.isFetchingNextPage}
            onLoadMore={() => void children.fetchNextPage()}
          />
        </>
      )}
    </div>
  );
}
